import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, deleteDoc, orderBy, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import { v4 as uuidv4 } from 'uuid';

export const FirestoreService = {
  // User operations
  async getUserProfile(userId) {
    const userRef = doc(db, 'users', userId);
    const docSnap = await getDoc(userRef);
    return docSnap.exists() ? { userId, ...docSnap.data() } : null;
  },

  async createUser(userId, userData) {
    console.log('Creating user:', userId, userData);
    const userRef = doc(db, 'users', userId);
    const userDocument = {
      userId,
      ...userData
    };
    await setDoc(userRef, userDocument);
    console.log('User created successfully');
    return { userId, ...userDocument };
  },

  async updateUser(userId, updates) {
    console.log('Updating user:', userId, updates);
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, updates, { merge: true });
    console.log('User updated successfully');
  },

  // Organization operations
  async getOrganization(organizationId) {
    const orgRef = doc(db, 'organizations', organizationId);
    const docSnap = await getDoc(orgRef);
    return docSnap.exists() ? docSnap.data() : null;
  },

  async getOrganizationByHash(orgHash) {
    const orgsRef = collection(db, 'organizations');
    const q = query(orgsRef, where('orgHash', '==', orgHash));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    // Return the first organization found with this hash
    const doc = querySnapshot.docs[0];
    return { organizationId: doc.id, ...doc.data() };
  },

  async createOrganization(organizationId, orgData) {
    const orgRef = doc(db, 'organizations', organizationId);
    const organizationDocument = {
      organizationId,
      ...orgData
    };
    await setDoc(orgRef, organizationDocument);
    return organizationDocument;
  },

  async updateOrganization(organizationId, updates) {
    console.log('Updating organization:', organizationId, updates);
    const orgRef = doc(db, 'organizations', organizationId);
    await updateDoc(orgRef, updates);
    console.log('Organization updated successfully');
  },

  // Dashboard operations (NEW SCHEMA)
  async getVersions(dashboardId) {
    const versionsRef = collection(db, 'dashboards', dashboardId, 'versions');
    const q = query(versionsRef, orderBy('createdAt', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ versionId: d.id, ...d.data() }));
  },

  async getDashboard(dashboardId) {
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    const [docSnap, versions] = await Promise.all([
      getDoc(dashboardRef),
      this.getVersions(dashboardId)
    ]);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        dashboardId,
        screenId: dashboardId, // For backward compatibility
        ...data,
        versions // Always from subcollection
      };
    }

    return null;
  },

  // Legacy Dashboard operations (OLD SCHEMA - for backward compatibility)
  async getLegacyDashboard(screenId) {
    const screenRef = doc(db, 'screens', screenId);
    const docSnap = await getDoc(screenRef);
    if (!docSnap.exists()) return null;

    const data = docSnap.data();
    // Handle legacy format - if no current/versions structure, migrate it
    if (!data.current && data.widgets) {
      return {
        screenId: data.screenId,
        organizationId: data.organizationId,
        current: {
          widgets: data.widgets,
          settings: data.settings || { bgColor: '#fcfbfa', bgImage: '', }
        },
        versions: [{
          versionId: uuidv4(),
          widgets: data.widgets,
          settings: data.settings || { bgColor: '#fcfbfa', bgImage: '', },
          createdAt: new Date()
        }]
      };
    }

    return data;
  },

  async getDashboardByOrganization(organizationId) {
    const screensRef = collection(db, 'screens');
    const q = query(screensRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    // Return the first dashboard found for this organization
    const data = querySnapshot.docs[0].data();

    // Handle legacy format - if no current/versions structure, migrate it
    if (!data.current && data.widgets) {
      const migratedData = {
        screenId: data.screenId,
        organizationId: data.organizationId,
        current: {
          widgets: data.widgets,
          settings: data.settings || { bgColor: '#fcfbfa', bgImage: '', }
        },
        versions: [{
          versionId: uuidv4(),
          widgets: data.widgets,
          settings: data.settings || { bgColor: '#fcfbfa', bgImage: '', },
          createdAt: new Date()
        }]
      };
      return migratedData;
    }

    return data;
  },

  async createDashboard(organizationId, options = {}) {
    const {
      name = 'דשבורד',
      description = '',
      createInitialVersion = true,  // ברירת מחדל: יוצר גירסה ראשונית
      templateWidgets = null,       // ווידג'טים מתבנית (אופציונלי)
      templateSettings = null       // הגדרות מתבנית (אופציונלי)
    } = options;

    const dashboardId = uuidv4();
    const dashboardRef = doc(db, 'dashboards', dashboardId);

    const defaultSettings = { bgColor: '#f0ece6', bgImage: '', borderRadius: 10 };

    // פונקציה ליצירת widgetId חדשים לכל ווידג'ט בתבנית
    const cloneTemplateWidgets = (widgets) => {
      if (!widgets || widgets.length === 0) return [];

      return widgets.map(widget => ({
        ...widget,
        widgetId: uuidv4(),
        createdAt: new Date()
      }));
    };

    // שימוש בווידג'טים והגדרות מהתבנית אם קיימים, אחרת מערך ריק
    const finalWidgets = templateWidgets ? cloneTemplateWidgets(templateWidgets) : [];
    const finalSettings = templateSettings || defaultSettings;

    // New structure with current (no versions array - stored in subcollection)
    const dashboardData = {
      dashboardId,
      screenId: dashboardId, // For backward compatibility
      organizationId,
      name,
      description,
      current: {
        widgets: finalWidgets,
        settings: finalSettings
      },
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(dashboardRef, dashboardData);

    // יצירת גירסה ראשונית בתת-אוסף
    if (createInitialVersion) {
      const initialVersionId = uuidv4();
      const versionRef = doc(db, 'dashboards', dashboardId, 'versions', initialVersionId);
      await setDoc(versionRef, {
        versionId: initialVersionId,
        widgets: finalWidgets,
        settings: finalSettings,
        createdAt: new Date()
      });
    }

    if (createInitialVersion) {
      console.log('✅ Dashboard created with initial version - visible immediately');
    } else {
      console.log('✅ Dashboard created without version - will be visible after first publish');
    }

    if (templateWidgets) {
      console.log('🎨 Dashboard created with template:', finalWidgets.length, 'widgets (with new IDs)');
    } else {
      console.log('📝 Dashboard created empty - ready for customization');
    }

    return dashboardData;
  },

  async updateDashboard(dashboardId, widgets, settings) {
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    const updateData = {
      current: {
        widgets,
        settings: settings || { bgColor: '#fcfbfa', bgImage: '', }
      },
      updatedAt: new Date()
    };
    await setDoc(dashboardRef, updateData, { merge: true });
    console.log('✅ Dashboard updated');
  },

  async publishDashboard(dashboardId, widgets, settings, versionLimit = 5) {
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    const docSnap = await getDoc(dashboardRef);

    if (!docSnap.exists()) {
      throw new Error('Dashboard not found');
    }

    // Write new version to subcollection
    const newVersionId = uuidv4();
    const versionRef = doc(db, 'dashboards', dashboardId, 'versions', newVersionId);
    const newVersion = {
      versionId: newVersionId,
      widgets,
      settings,
      createdAt: new Date()
    };
    await setDoc(versionRef, newVersion);

    // Enforce version limit - delete oldest versions that exceed the cap
    const versionsRef = collection(db, 'dashboards', dashboardId, 'versions');
    const allVersionsSnap = await getDocs(query(versionsRef, orderBy('createdAt', 'asc')));
    const excessCount = allVersionsSnap.docs.length - versionLimit;
    if (excessCount > 0) {
      await Promise.all(allVersionsSnap.docs.slice(0, excessCount).map(d => deleteDoc(d.ref)));
    }

    // Update latestVersionId and remove legacy versions array from main document
    const currentData = docSnap.data();
    const mainDocUpdate = { latestVersionId: newVersionId };
    if (currentData.versions !== undefined) {
      mainDocUpdate.versions = deleteField();
    }
    await setDoc(dashboardRef, mainDocUpdate, { merge: true });

    const isFirstPublish = !currentData.versions || currentData.versions.length === 0;
    console.log(isFirstPublish ? '✅ First version published - dashboard is now visible!' : '✅ Version published successfully');

    return newVersion;
  },

  async loadVersion(dashboardId, versionId, isPublicView = false) {
    const versionRef = doc(db, 'dashboards', dashboardId, 'versions', versionId);
    const versionSnap = await getDoc(versionRef);

    if (!versionSnap.exists()) {
      throw new Error('Version not found');
    }

    const version = { versionId, ...versionSnap.data() };

    if (isPublicView) {
      return version;
    }

    // Update current to match the selected version (only for authenticated users)
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    await setDoc(dashboardRef, {
      current: {
        widgets: version.widgets,
        settings: version.settings
      }
    }, { merge: true });

    console.log('✅ Version loaded successfully');
    return version;
  },

  // Catalog operations
  async getAppCatalog() {
    const catalogRef = collection(db, 'APP-CATALOG');
    const snapshot = await getDocs(catalogRef);
    const catalog = [];

    snapshot.forEach(doc => {
      catalog.push({ id: doc.id, ...doc.data() });
    });

    return catalog;
  },

  // Predefined Templates operations
  async getPredefinedTemplates() {
    const templatesRef = collection(db, 'predefinedTemplates');
    const snapshot = await getDocs(templatesRef);

    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  },

  // ============================================
  // Screens Operations (New Schema)
  // ============================================

  async getScreen(screenId) {
    const screenRef = doc(db, 'screens', screenId);
    const docSnap = await getDoc(screenRef);
    return docSnap.exists() ? { screenId, ...docSnap.data() } : null;
  },

  async getScreensByOrganization(organizationId) {
    const screensRef = collection(db, 'screens');
    const q = query(screensRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);

    const screens = [];
    querySnapshot.forEach(doc => {
      screens.push({ screenId: doc.id, ...doc.data() });
    });

    return screens;
  },

  async createScreen(organizationId, screenData) {
    const screenId = uuidv4();
    const screenRef = doc(db, 'screens', screenId);

    const screenDocument = {
      screenId,
      organizationId,
      name: screenData.name || 'מסך חדש',
      description: screenData.description || '',
      url: screenData.url || '',
      schedule: screenData.schedule || {}, // אובייקט עם מפתחות 0-6 (ימים)
      settings: screenData.settings || {},
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(screenRef, screenDocument);
    return screenDocument;
  },

  // יצירת מסך עם screenId ספציפי (לשימוש ביצירת ארגון)
  async createScreenWithId(screenId, organizationId, screenData) {
    const screenRef = doc(db, 'screens', screenId);

    const screenDocument = {
      screenId,
      organizationId,
      name: screenData.name || 'מסך חדש',
      description: screenData.description || '',
      url: screenData.url || '',
      schedule: screenData.schedule || {}, // אובייקט עם מפתחות 0-6 (ימים)
      settings: screenData.settings || {},
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(screenRef, screenDocument);
    return screenDocument;
  },

  async updateScreen(screenId, updates) {
    const screenRef = doc(db, 'screens', screenId);
    // שימוש ב-updateDoc במקום setDoc - יותר מהיר!
    await updateDoc(screenRef, { ...updates, updatedAt: new Date() });
  },

  async deleteScreen(screenId) {
    const screenRef = doc(db, 'screens', screenId);
    await deleteDoc(screenRef);
  },

  // ============================================
  // Playlists Operations (New Schema)
  // ============================================

  async getPlaylist(playlistId) {
    const playlistRef = doc(db, 'playlists', playlistId);
    const docSnap = await getDoc(playlistRef);
    return docSnap.exists() ? { playlistId, ...docSnap.data() } : null;
  },

  async getPlaylistsByOrganization(organizationId) {
    const playlistsRef = collection(db, 'playlists');
    const q = query(playlistsRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);

    const playlists = [];
    querySnapshot.forEach(doc => {
      playlists.push({ playlistId: doc.id, ...doc.data() });
    });

    return playlists;
  },

  // שאילתה חדשה - פלייליסטים לפי מסך
  async getPlaylistsByScreen(screenId) {
    const playlistsRef = collection(db, 'playlists');
    const q = query(playlistsRef, where('screenId', '==', screenId));
    const querySnapshot = await getDocs(q);

    const playlists = [];
    querySnapshot.forEach(doc => {
      playlists.push({ playlistId: doc.id, ...doc.data() });
    });

    return playlists;
  },

  async createPlaylist(organizationId, playlistData, screenId = null) {
    const playlistId = uuidv4();
    const playlistRef = doc(db, 'playlists', playlistId);

    const playlistDocument = {
      playlistId,
      organizationId,
      screenId: screenId || null, // שדה חדש - מזהה המסך
      name: playlistData.name || 'מערך תצוגה חדש',
      description: playlistData.description || '',
      items: playlistData.items || [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    await setDoc(playlistRef, playlistDocument);
    return playlistDocument;
  },

  async updatePlaylist(playlistId, updates) {
    const playlistRef = doc(db, 'playlists', playlistId);
    await setDoc(playlistRef, { ...updates, updatedAt: new Date() }, { merge: true });
  },

  async deletePlaylist(playlistId) {
    const playlistRef = doc(db, 'playlists', playlistId);
    await deleteDoc(playlistRef);
  },

  // ============================================
  // Dashboards Operations (Updated for New Schema)
  // ============================================

  async getDashboardsByOrganization(organizationId) {
    const dashboardsRef = collection(db, 'dashboards');
    const q = query(dashboardsRef, where('organizationId', '==', organizationId));
    const querySnapshot = await getDocs(q);

    const dashboards = [];
    querySnapshot.forEach(doc => {
      dashboards.push({ dashboardId: doc.id, ...doc.data() });
    });

    return dashboards;
  },

  // Update dashboard with flexible updates object (name, description, versions, etc.)
  // For updating widgets/settings only, use updateDashboard() instead
  async updateDashboardNew(dashboardId, updates) {
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    await setDoc(dashboardRef, { ...updates, updatedAt: new Date() }, { merge: true });
  },

  // Delete dashboard from dashboards collection
  async deleteDashboardNew(dashboardId) {
    const dashboardRef = doc(db, 'dashboards', dashboardId);
    await deleteDoc(dashboardRef);
  },

  // ============================================
  // Admin Operations
  // ============================================

  async getAllOrganizations() {
    const orgsRef = collection(db, 'organizations');
    const snapshot = await getDocs(orgsRef);
    return snapshot.docs.map(doc => ({ organizationId: doc.id, ...doc.data() }));
  },

  async getAllLicenses() {
    const licensesRef = collection(db, 'licenses');
    const snapshot = await getDocs(licensesRef);
    const map = {};
    snapshot.docs.forEach(doc => { map[doc.id] = { organizationId: doc.id, ...doc.data() }; });
    return map;
  },

  async getScreensCountByOrg() {
    const screensRef = collection(db, 'screens');
    const snapshot = await getDocs(screensRef);
    const counts = {};
    snapshot.docs.forEach(doc => {
      const orgId = doc.data().organizationId;
      counts[orgId] = (counts[orgId] || 0) + 1;
    });
    return counts;
  }
};


